import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button, useSidebar } from '@erp/ui';
import { eventToShortcut, formatShortcut, useQuickLinks } from '@/hooks/use-quick-links';
import { findCatalogEntry } from '@/lib/quick-links-catalog';

/**
 * Desktop-only strip of user-configurable quick-link buttons, rendered next to
 * the sidebar trigger in the top header. Also registers a global keydown
 * listener so each link's shortcut navigates straight to its target route.
 */
export function QuickLinksBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { links } = useQuickLinks();

  useEffect(() => {
    if (isMobile) return;
    const shortcuts = links.filter((l) => l.shortcut);
    if (shortcuts.length === 0) return;

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return;
        }
      }
      const pressed = eventToShortcut(e);
      if (!pressed) return;
      const match = shortcuts.find((l) => l.shortcut === pressed);
      if (!match) return;
      const entry = findCatalogEntry(match.key);
      if (!entry) return;
      e.preventDefault();
      navigate(entry.path);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [links, isMobile, navigate]);

  if (isMobile || links.length === 0) return null;

  return (
    <div className="hidden lg:flex items-center gap-1">
      {links.map((link) => {
        const entry = findCatalogEntry(link.key);
        if (!entry) return null;
        const label = t(entry.labelKey);
        return (
          <Button
            key={link.key}
            variant="ghost"
            size="sm"
            asChild
            aria-label={label}
            className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Link to={entry.path}>
              <span className="text-sm">{label}</span>
              {link.shortcut && (
                <kbd className="ms-1 rounded border border-border bg-muted/50 px-1 py-0.5 font-mono text-[10px] leading-none text-muted-foreground">
                  {formatShortcut(link.shortcut)}
                </kbd>
              )}
            </Link>
          </Button>
        );
      })}
    </div>
  );
}
